// ファイルバリデーション関連のユーティリティ

import { FileValidationError, FileUploadConfig, DEFAULT_FILE_CONFIG, FileType } from '../types';

export interface ValidationContext {
  existingFiles: File[];
  config: FileUploadConfig;
}

export interface FileValidationResult {
  valid: boolean;
  errors: FileValidationError[];
  acceptedFiles: File[];
  rejectedFiles: File[];
  duplicateFiles: File[];
  summary: {
    total: number;
    accepted: number;
    rejected: number;
    duplicates: number;
  };
}

export const validateFile = (
  file: File, 
  config: FileUploadConfig = DEFAULT_FILE_CONFIG
): FileValidationError | null => {
  // ファイルサイズチェック
  if (file.size > config.maxFileSize) {
    return {
      type: 'size',
      fileName: file.name,
      message: `ファイル "${file.name}" が大きすぎます (最大${Math.round(config.maxFileSize / 1024 / 1024)}MB)`
    };
  }

  // ファイルタイプチェック
  if (!config.allowedTypes.includes(file.type)) {
    return {
      type: 'format',
      fileName: file.name,
      message: `ファイル "${file.name}" は対応していない形式です`
    };
  }

  return null;
};

export const validateFiles = (
  files: File[], 
  config: FileUploadConfig = DEFAULT_FILE_CONFIG
): FileValidationError[] => {
  const errors: FileValidationError[] = [];

  // ファイル数チェック
  if (files.length > config.maxFiles) {
    errors.push({
      type: 'count',
      fileName: '',
      message: `ファイル数が上限を超えています (最大${config.maxFiles}件)`
    });
  }

  // 各ファイルのバリデーション
  files.forEach(file => {
    const error = validateFile(file, config);
    if (error) {
      errors.push(error);
    }
  });

  return errors;
};

export const getFileType = (file: File): FileType => {
  const mimeType = file.type.toLowerCase();
  
  if (mimeType.includes('pdf')) {
    return 'pdf';
  } else if (mimeType.includes('csv') || mimeType === 'text/csv') {
    return 'csv';
  } else if (mimeType.includes('excel') || mimeType.includes('sheet')) {
    return 'excel';
  } else if (mimeType.includes('word') || mimeType.includes('document')) {
    return 'word';
  } else if (
    mimeType.includes('presentation') ||
    mimeType.includes('powerpoint') ||
    mimeType === 'application/vnd.ms-powerpoint'
  ) {
    return 'ppt';
  } else if (mimeType.includes('image')) {
    return 'image';
  } else {
    return 'other';
  }
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' bytes';
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  else return (bytes / 1048576).toFixed(1) + ' MB';
};

export const generateFileId = (): string => {
  return `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const checkDuplicateFiles = (newFiles: File[], existingFiles: File[]): File[] => {
  const existingNames = new Set(existingFiles.map(f => f.name));
  return newFiles.filter(file => existingNames.has(file.name));
};

export const checkTotalFileCount = (
  newCount: number, 
  existingCount: number, 
  maxFiles: number
): FileValidationError | null => {
  const total = newCount + existingCount;
  if (total > maxFiles) {
    return {
      type: 'count',
      fileName: '',
      message: `ファイル数が上限を超えています (最大${maxFiles}件)`
    };
  }
  return null;
};

export const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1 || lastDotIndex === 0) {
    return '';
  }
  return fileName.substring(lastDotIndex + 1).toLowerCase();
};

export const isFileTypeAllowed = (file: File, allowedExtensions: string[]): boolean => {
  const extension = getFileExtension(file.name);
  return allowedExtensions.some(ext => ext.toLowerCase() === extension);
};

export const validateFilesWithContext = (
  files: File[], 
  context: ValidationContext
): FileValidationResult => {
  const errors: FileValidationError[] = [];
  const acceptedFiles: File[] = [];
  const rejectedFiles: File[] = [];
  
  // Check duplicate files
  const duplicateFiles = checkDuplicateFiles(files, context.existingFiles);
  if (duplicateFiles.length > 0) {
    duplicateFiles.forEach(file => {
      errors.push({
        type: 'duplicate',
        fileName: file.name,
        message: `ファイル "${file.name}" は既にアップロードされています`
      });
    });
  }
  
  // Check total file count
  const totalCountError = checkTotalFileCount(
    files.length, 
    context.existingFiles.length, 
    context.config.maxFiles
  );
  if (totalCountError) {
    errors.push(totalCountError);
  }
  
  // Validate each file
  files.forEach(file => {
    const validationError = validateFile(file, context.config);
    const isDuplicate = duplicateFiles.includes(file);
    
    if (validationError || isDuplicate) {
      rejectedFiles.push(file);
      if (validationError && !isDuplicate) {
        errors.push(validationError);
      }
    } else {
      acceptedFiles.push(file);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors,
    acceptedFiles,
    rejectedFiles,
    duplicateFiles,
    summary: {
      total: files.length,
      accepted: acceptedFiles.length,
      rejected: rejectedFiles.length,
      duplicates: duplicateFiles.length
    }
  };
};
